<script setup lang="ts">
import type { TableSelectionState } from '@/hooks/useHybridlyTable';

import { bulkCreate as bulkCreateFinancialAid } from '@/api/FinancialAidApi';
import { bulkCreate as bulkCreateCollegeApplication } from '@/api/CollegeApplicationApi';
import { bulkCreate as bulkCreateScholarship } from '@/api/ScholarshipApi';

import { useAuth } from '@/hooks/useAuth';

import MenuButton from '@/components/button/MenuButton.vue';
import MenuItem from '@/components/menu/MenuItem.vue';
import ExternalLinkAlt from '@/icons/line/external-link-alt.svg';

import BulkCreateStudentInteractionDialog from '@/domains/students/components/dialogs/BulkCreateStudentInteractionDialog.vue';
import BulkCreateStudentNoteDialog from '@/domains/students/components/dialogs/BulkCreateStudentNoteDialog.vue';
import BulkUpdateEnrollmentStatusDialog from '@/domains/students/components/dialogs/BulkUpdateEnrollmentStatusDialog.vue';
import BulkUpdateStudentPostSecondaryPlanDialog from '@/domains/students/components/dialogs/BulkUpdateStudentPostSecondaryPlanDialog.vue';

type EnumData = App.Base.Data.EnumData;

const props = defineProps<{
  tableSelectionState: TableSelectionState;
  studentCount: number;
  enrollmentStatuses: EnumData[];
  postSecondaryPlans: EnumData[];
  defaultSchool: App.Configuration.Data.SchoolData;
}>();

const { hasPermission } = useAuth();
const queryParameters = useQueryParameters();

const [isInteractionOpen, toggleInteractionOpen] = useToggle(false);
const [isPostSecondaryPlanOpen, togglePostSecondaryPlanOpen] = useToggle(false);
const [isNoteOpen, toggleNoteOpen] = useToggle(false);
const [isEnrollmentStatusOpen, toggleEnrollmentStatusOpen] = useToggle(false);

const isAddOrUpdateMenuOpen = ref<boolean>(false);
const isUpdateFinancialAidLoading = ref(false);
const isAddCollegeApplicationLoading = ref(false);
const isAddScholarshipLoading = ref(false);

function handleToggleAddOrUpdateMenuOpenState() {
  isAddOrUpdateMenuOpen.value = !isAddOrUpdateMenuOpen.value;
}

function handleAddOrUpdateMenuInteractOutside() {
  isAddOrUpdateMenuOpen.value = false;
}

async function handleUpdateFinancialAidClick() {
  isUpdateFinancialAidLoading.value = true;

  const response = await bulkCreateFinancialAid({
    ids: props.tableSelectionState.selectedRowIds || [],
    filters: queryParameters.filters || null,
    all: props.tableSelectionState.areAllRecordsSelected
  });

  isUpdateFinancialAidLoading.value = false;
  handleToggleAddOrUpdateMenuOpenState();
  window.open(response.session_url, '_blank', 'noopener,noreferrer');
}

async function handleAddCollegeApplicationClick() {
  isAddCollegeApplicationLoading.value = true;

  const response = await bulkCreateCollegeApplication({
    ids: props.tableSelectionState.selectedRowIds || [],
    filters: queryParameters.filters || null,
    all: props.tableSelectionState.areAllRecordsSelected
  });

  isAddCollegeApplicationLoading.value = false;
  handleToggleAddOrUpdateMenuOpenState();
  window.open(response.session_url, '_blank', 'noopener,noreferrer');
}

async function handleAddScholarshipClick() {
  isAddScholarshipLoading.value = true;

  const response = await bulkCreateScholarship({
    ids: props.tableSelectionState.selectedRowIds || [],
    filters: queryParameters.filters || null,
    all: props.tableSelectionState.areAllRecordsSelected
  });

  isAddScholarshipLoading.value = false;
  handleToggleAddOrUpdateMenuOpenState();
  window.open(response.session_url, '_blank', 'noopener,noreferrer');
}
</script>

<template>
  <div>
    <MenuButton
      :isOpen="isAddOrUpdateMenuOpen"
      buttonColor="slate"
      buttonLabel="Add or update"
      buttonSize="sm"
      buttonVariant="outlined"
      @click="handleToggleAddOrUpdateMenuOpenState"
      @onInteractOutside="handleAddOrUpdateMenuInteractOutside"
    >
      <MenuItem
        v-if="hasPermission('create-interaction')"
        label="Add Interaction"
        description="Add the same interaction for all selected students"
        @click="() => toggleInteractionOpen()"
      />
      <MenuItem
        v-if="hasPermission('create-note')"
        label="Add Note"
        description="Add the same note for all selected students"
        @click="() => toggleNoteOpen()"
      />
      <MenuItem
        label="Add College Application"
        description="Add college application for all selected students using bulkinator"
        :iconRight="ExternalLinkAlt"
        :isLoading="isAddCollegeApplicationLoading"
        @click="handleAddCollegeApplicationClick()"
      />
      <MenuItem
        label="Add Scholarship"
        description="Add scholarship for all selected students using bulkinator"
        :iconRight="ExternalLinkAlt"
        :isLoading="isAddScholarshipLoading"
        @click="handleAddScholarshipClick()"
      />
      <MenuItem
        label="Add Postsecondary Plan"
        description="Add the same postsecondary plan for all selected students"
        @click="() => togglePostSecondaryPlanOpen()"
      />
      <MenuItem
        label="Change Enrollment Status"
        description="Change status for all selected students"
        @click="() => toggleEnrollmentStatusOpen()"
      />
      <MenuItem
        label="Update Financial Aid"
        description="Update financial aid for all selected students using bulkinator"
        :iconRight="ExternalLinkAlt"
        :isLoading="isUpdateFinancialAidLoading"
        @click="handleUpdateFinancialAidClick()"
      />
    </MenuButton>

    <BulkCreateStudentInteractionDialog
      v-if="isInteractionOpen"
      isOpen
      :selectedStudentsCount="studentCount"
      :tableSelectionState="tableSelectionState"
      @onCancel="() => toggleInteractionOpen()"
      @onClose="() => toggleInteractionOpen()"
    />

    <BulkCreateStudentNoteDialog
      v-if="isNoteOpen"
      isOpen
      :selectedStudentsCount="studentCount"
      :tableSelectionState="tableSelectionState"
      @onCancel="() => toggleNoteOpen()"
      @onClose="() => toggleNoteOpen()"
    />

    <BulkUpdateStudentPostSecondaryPlanDialog
      v-if="isPostSecondaryPlanOpen"
      isOpen
      :postSecondaryPlans="postSecondaryPlans"
      :selectedStudentsCount="studentCount"
      :tableSelectionState="tableSelectionState"
      @onCancel="() => togglePostSecondaryPlanOpen()"
      @onClose="() => togglePostSecondaryPlanOpen()"
    />

    <BulkUpdateEnrollmentStatusDialog
      v-if="isEnrollmentStatusOpen"
      :enrollmentStatuses="enrollmentStatuses"
      :selectedStudentsCount="studentCount"
      :tableSelectionState="tableSelectionState"
      isOpen
      @onCancel="() => toggleEnrollmentStatusOpen()"
      @onClose="() => toggleEnrollmentStatusOpen()"
    />
  </div>
</template>
